import * as PIXI from 'pixi.js';
import { auth, hasSession } from '../auth/FirebaseAuth';
import { logDev } from '../utils/LogDev';
import { ApiManager } from './ApiManager';
import { GuestStorageManager } from './GuestStorageManager';

import { HistoryManager, SnapshotData, SpriteSheetData } from './HistoryManager';
import { ClipboardManager } from './ClipboardManager';
import { EditorManager } from './EditorManager';
import { LifeCycleManager } from './LifeCycleManager';
import { ColorPicker } from './ColorPicker';
import { ExportModal } from '../dialogs/ExportModal';
// @ts-ignore
import GIF from 'gif.js';

export class SpriteLab {
    public container: HTMLDivElement;
    public historyManager: HistoryManager = new HistoryManager(this);
    public clipboardManager: ClipboardManager = new ClipboardManager(this);
    public editorManager: EditorManager;
    public lifeCycleManager!: LifeCycleManager;
    public colorPicker: ColorPicker | null = null;
    public apiManager: ApiManager = new ApiManager();
    public explorerTree: HTMLDivElement | null = null;
    public previewContainer: HTMLDivElement | null = null;
    public previewApp: PIXI.Application | null = null;
    public sessionSheets: Map<string, SpriteSheetData> = new Map();
    public isLoading: boolean = false;
    public isLoadingProjects: boolean = true;
    private loadingProjectPromise: Promise<void> | null = null;
    private loadingSheetsPromise: Map<string, Promise<void>> = new Map();
    private uiReady: boolean = false;
    public envContainer!: PIXI.Container;
    public spriteContainer!: PIXI.Container;
    public worldLayer!: PIXI.Container;
    public cameraState = {
        zoom: 1.0,
        offset: { x: 0, y: 0 },
        isDragging: false,
        lastPointer: { x: 0, y: 0 },
        startPointer: { x: 0, y: 0 },
        dragStartTime: 0
    };
    public projectId: string = 'main';
    public itemId: string = 'sprite';
    public lastItemId: string = '';
    public lastProjectId: string = '';
    public projects: string[] = [];
    public projectTree: { name: string, createdAt?: any, updatedAt?: any, sprites: string[] }[] = [];
    public sortConfig: { key: 'name' | 'createdAt', order: 'asc' | 'desc' } = { key: 'createdAt', order: 'asc' };
    public expandedProjects: Set<string> = new Set();
    public initializedExpansion: boolean = false;
    public currentTool: 'mouse' | 'brush' | 'eraser' | 'bucket' | 'picker' | 'grid' | 'rotate' | 'transpose' | 'scale' = 'brush';
    public previousTool: 'mouse' | 'brush' | 'eraser' | 'bucket' | 'picker' | 'grid' | 'rotate' | 'transpose' | 'scale' = 'brush';
    public toolState = {
        brush: { color: '#000000', secondaryColor: '#ffffff', size: 1, mode: 'color' as 'color' | 'transparent' },
        eraser: { color: '#ffffff', secondaryColor: '#000000', size: 1, mode: 'transparent' as 'transparent' | 'color' },
        grid: { active: false, divisions: 4 },
        bucket: { tolerance: 0 }

    };
    public selectedFrame: { col: number, row: number, free?: boolean, px?: number, py?: number, pw?: number, ph?: number } | null = null;
    public pastedData: {
        img: HTMLImageElement | HTMLCanvasElement,
        x: number,
        y: number,
        w: number,
        h: number,
        rotation: number, // in radians
        activeHandle: 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'rotate' | null,
        startX: number,
        startY: number,
        startW: number,
        startH: number,
        startPosX: number,
        startPosY: number,
        startAncX: number,
        startAncY: number,
        startLX: number,
        startLY: number,
        startRotation: number
    } | null = null;
    public dataCanvas: HTMLCanvasElement = document.createElement('canvas');
    public currentBase64: string = '';
    public originalBase64: string | null = null;
    public lastRequestId: number = 0;
    public lastSheetX: number = 0;
    public lastSheetY: number = 0;
    public transformState = {
        scale: 1,
        pX: 0,
        pY: 0
    };
    public defaultScale: number = 1;
    public updateSheetTransform: (() => void) | null = null;
    public renderToolPreview!: (sheetX: number, sheetY: number, viewCanvas: HTMLCanvasElement, dataCanvas: HTMLCanvasElement) => void;
    public updatePasteOverlay!: () => void;

    public t: (key: any) => string = (key) => key;

    public setTranslator(translator: (key: any) => string) {
        this.t = translator;
    }

    constructor() {
        const t0 = performance.now();
        const T = () => `+${(performance.now() - t0).toFixed(0)}ms`;
        console.log(`[SpriteLab] [${T()}] Constructor starting`);
        this.editorManager = new EditorManager(this);
        console.log(`[SpriteLab] [${T()}] EditorManager ready`);
        this.lifeCycleManager = new LifeCycleManager(this);
        console.log(`[SpriteLab] [${T()}] LifeCycleManager ready`);
        this.container = document.createElement('div');
        this.container.id = 'sprite-lab-panel';
        console.log(`[SpriteLab] [${T()}] Constructor finished`);
    }
    public mount(parent: HTMLElement) {
        logDev('[SpriteLab] mount() called');
        if (!this.uiReady) {
            this.lifeCycleManager.setupUI();
            this.uiReady = true;
        }
        this.lifeCycleManager.mount(parent);
    }
    public async show() {
        await this.lifeCycleManager.show();
    }
    public hide() {
        this.lifeCycleManager.hide();
    }
    public updateMobileUI(forceScroll = true) {
        this.lifeCycleManager.updateMobileUI(forceScroll);
    }
    public renderExplorer() {
        this.lifeCycleManager.renderExplorer();
    }
    public renderTabs(states: string[]) {
        this.lifeCycleManager.renderTabs(states);
    }
    public renderHistoryList() {
        this.lifeCycleManager.renderHistoryList();
    }
    public setupModalListeners() {
        this.lifeCycleManager.setupModalListeners();
    }
    public updateCursor() {
        this.editorManager.updateCursor();
    }
    public async handleRenameSpriteInternal(oldName: string, newName: string, projectId: string = this.projectId): Promise<boolean> {
        if (!oldName || !newName) return false;

        if (!auth.currentUser) {
            try {
                await GuestStorageManager.renameSpriteKeys(projectId, oldName, newName);
                const proj = this.projectTree.find(p => p.name === projectId);
                if (proj) {
                    const idx = proj.sprites.indexOf(oldName);
                    if (idx !== -1) proj.sprites[idx] = newName;
                }
                const oldPrefix = `${projectId}:${oldName}:`;
                for (const [k, v] of Array.from(this.sessionSheets.entries())) {
                    if (k.startsWith(oldPrefix)) {
                        const newKey = `${projectId}:${newName}:${k.slice(oldPrefix.length)}`;
                        this.sessionSheets.set(newKey, v);
                        this.sessionSheets.delete(k);
                    }
                }
                if (this.itemId === oldName && this.projectId === projectId) {
                    this.itemId = newName;
                    this.lastItemId = newName;
                    this.lastProjectId = projectId;
                }
                await this.loadProjectList();
                return true;
            } catch (err) {
                console.error('[SpriteLab] IDB renameSprite failed:', err);
                return false;
            }
        }

        const success = await this.apiManager.renameSprite(projectId, oldName, newName);
        if (success) {
            if (this.itemId === oldName && this.projectId === projectId) {
                this.itemId = newName;
                this.lastItemId = newName;
                this.lastProjectId = projectId;
            }
            await this.loadProjectList();
        }
        return success;
    }
    public saveSheetToMemory(overrideSprite?: string, overrideSheet?: string) {
        const sprite = overrideSprite || this.itemId;
        const sheet = overrideSheet || this.currentSheet;
        logDev(`[SpriteLab] saveSheetToMemory: ${sprite}:${sheet}`);
        if (!sprite || !sheet) return;
        const key = `${this.projectId}:${sprite}:${sheet}`;
        const base64 = this.dataCanvas.toDataURL();
        const wInput = this.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fInput = this.container.querySelector('#sl-meta-frames') as HTMLInputElement;
        const existing = this.sessionSheets.get(key);
        const history: SnapshotData[] = existing ? existing.history : [{
            base64: base64,
            width: wInput ? parseInt(wInput.value) || 32 : 32,
            height: hInput ? parseInt(hInput.value) || 32 : 32,
            frames: fInput ? parseInt(fInput.value) || 1 : 1,
            animation: existing?.animation || 'sequence',
            interval: existing?.interval || 0.2,
            palette: [...(this.colorPicker?.getPalette() || [])]
        }];
        const historyIndex = existing ? existing.historyIndex : 0;
        this.sessionSheets.set(key, {
            base64: base64,
            originalBase64: existing ? existing.originalBase64 : base64,
            width: wInput ? parseInt(wInput.value) || 32 : 32,
            height: hInput ? parseInt(hInput.value) || 32 : 32,
            frames: fInput ? parseInt(fInput.value) || 1 : 1,
            orientation: existing?.orientation || 'horizontal',
            animation: existing?.animation || 'sequence',
            interval: existing?.interval || 0.2,
            isDirty: this.checkDirty(),
            history,
            historyIndex,
            palette: [...(this.colorPicker?.getPalette() || [])],
            baseSnapshot: existing ? existing.baseSnapshot : history[0]
        });
    }
    public async loadSheetFromMemory(spriteName: string, sheetName: string, suppressLoading: boolean = false): Promise<boolean> {
        const key = `${this.projectId}:${spriteName}:${sheetName}`;
        if (this.sessionSheets.has(key)) {
            const sheetData = this.sessionSheets.get(key)!;
            logDev(`[SheetDebug] loadSheetFromMemory HIT ${key}`);
            this.isLoading = true;
            const loader = this.container.querySelector('#sl-sheet-loading') as HTMLElement;
            if (loader && !suppressLoading) loader.style.display = 'flex';
            (this.container.querySelector('#sl-meta-width') as HTMLInputElement).value = sheetData.width.toString();
            (this.container.querySelector('#sl-meta-height') as HTMLInputElement).value = sheetData.height.toString();
            (this.container.querySelector('#sl-meta-frames') as HTMLInputElement).value = sheetData.frames.toString();
            this.updateInterval(sheetData.interval);
            if (this.colorPicker && sheetData.palette) {
                this.colorPicker.setPalette(sheetData.palette);
            }
            this.originalBase64 = sheetData.originalBase64;
            
            // Wait for image loading and preview update
            await new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = async () => {
                    const dataCanvas = this.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
                    const ctx = dataCanvas.getContext('2d');
                    dataCanvas.width = img.width;
                    dataCanvas.height = img.height;
                    ctx?.drawImage(img, 0, 0);
                    const canonicalBase64 = dataCanvas.toDataURL();
                    const isSessionDirty = sheetData.base64 !== sheetData.originalBase64;
                    if (!isSessionDirty) {
                        this.originalBase64 = canonicalBase64;
                        sheetData.originalBase64 = canonicalBase64;
                        sheetData.base64 = canonicalBase64;
                        const initialSnapshot = {
                            base64: canonicalBase64,
                            width: sheetData.width,
                            height: sheetData.height,
                            frames: sheetData.frames,
                            orientation: sheetData.orientation,
                            animation: sheetData.animation,
                            interval: sheetData.interval,
                            palette: [...(sheetData.palette || [])]
                        };
                        if (sheetData.history && sheetData.history.length <= 1) {
                            sheetData.history = [initialSnapshot];
                            sheetData.historyIndex = 0;
                            logDev(`[Snapshot] Initialized history during load for ${key}`);
                        }
                        if (!sheetData.baseSnapshot) {
                            sheetData.baseSnapshot = initialSnapshot;
                        }
                    } else {
                        this.originalBase64 = sheetData.originalBase64;
                    }
                    this.currentBase64 = canonicalBase64;
                    await this.updatePreview(canonicalBase64, true);
                    resolve();
                };
                img.onerror = () => resolve();
                img.src = sheetData.base64;
            });

            this.isLoading = false;
            if (loader) loader.style.display = 'none';
            this.checkDirty();
            this.updateButtonStates();
            return true;
        }
        logDev(`[SheetDebug] loadSheetFromMemory MISS ${key}`);
        return false;
    }
    public currentAnim: PIXI.AnimatedSprite | null = null;
    public currentSheet = 'main'; // Default
    public parsedSheets: string[] = ['main'];
    public onOpen: (() => void) | null = null;
    public onClose: (() => void) | null = null;
    public async loadSpriteList() {
        logDev('[SpriteLab] loadSpriteList() called for project: ' + this.projectId);
        try {
            const user = auth.currentUser;
            if (!user) {
                logDev('[SpriteLab] loadSpriteList() - no user, loading sprites from IndexedDB');
                try {
                    const guestTree = await GuestStorageManager.getProjectTree();
                    let proj = guestTree.find(p => p.name === this.projectId);
                    if (!proj) proj = this.projectTree.find(p => p.name === this.projectId);
                    const sprites = proj ? proj.sprites : [];
                    this.updateProjectInTree(this.projectId, sprites);
                } catch {
                    this.updateProjectInTree(this.projectId, []);
                }
                this.renderExplorer();
                return;
            }
            const idToken = await user.getIdToken();
            const headers: HeadersInit = {};
            if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
            const apiUrl = (import.meta as any).env.VITE_API_URL ? (import.meta as any).env.VITE_API_URL : '';
            const url = `${apiUrl}/api/sprite-lab/projects/${this.projectId}/sprites`;
            logDev(`[SpriteLab] loadSpriteList() fetching: ${url}`);
            const res = await fetch(url, { headers });
            logDev(`[SpriteLab] loadSpriteList() response: ${res.status} ${res.statusText}`);
            if (!res.ok) throw new Error(`Failed to load sprites: ${res.statusText}`);
            const sprites = await res.json();
            logDev(`[SpriteLab] loadSpriteList() got ${sprites.length} sprites: [${sprites.join(', ')}]`);
            this.updateProjectInTree(this.projectId, sprites);
            this.renderExplorer();
        } catch (e) {
            logDev(`[SpriteLab] loadSpriteList() error: ${e}`);
            console.error("Failed to load sprite list", e);
            this.updateProjectInTree(this.projectId, []);
            this.renderExplorer();
        }
    }
    private updateProjectInTree(projectId: string, sprites: string[]) {
        const p = this.projectTree.find(p => p.name === projectId);
        if (p) {
            p.sprites = sprites;
        } else {
            this.projectTree.push({ name: projectId, sprites });
            if (!this.projects.includes(projectId)) {
                this.projects.push(projectId);
            }
        }
        if (this.projectId === projectId && this.itemId && !sprites.includes(this.itemId)) {
            if (sprites.includes('sprite')) {
                this.itemId = 'sprite';
            } else if (sprites.length > 0) {
                this.itemId = sprites[0];
            } else {
                this.itemId = '';
            }
        }
    }
    async handleUpload() {
        const name = this.itemId;
        const sheet = this.currentSheet;
        const base64 = this.currentBase64;
        if (!base64 || !name) return alert(this.t('err_select_sprite'));
        const widthVal = (this.container.querySelector('#sl-meta-width') as HTMLInputElement).value;
        const heightVal = (this.container.querySelector('#sl-meta-height') as HTMLInputElement).value;
        const framesVal = (this.container.querySelector('#sl-meta-frames') as HTMLInputElement).value;
        const key = `${this.projectId}:${name}:${sheet}`;
        const sheetData = this.sessionSheets.get(key);
        const metadata = {
            width: parseInt(widthVal) || 32,
            height: parseInt(heightVal) || 32,
            frames: parseInt(framesVal) || 3,
            animationType: sheetData?.animation || 'sequence',
            interval: sheetData?.interval || 1.0,
            palette: [...(this.colorPicker?.getPalette() || [])]
        };
        const result = await this.apiManager.uploadSprite(this.projectId, name, sheet, base64, metadata);
        if (result.success) {
            alert(this.t('msg_upload_success').replace('{0}', result.filename));
            this.saveSheetToMemory(); // Update memory with dirty = false? 
            this.originalBase64 = base64;
            this.saveSheetToMemory(); // Now it will be clean
            this.checkDirty();
        } else {
            alert(this.t('msg_upload_failed').replace('{0}', result.error));
        }
    }
    public async checkSpriteAvailability(skipSheetLoad = false, suppressLoading = false) {
        const name = this.itemId;
        logDev(`[SpriteLab] checkSpriteAvailability: ${name} (skipSheetLoad=${skipSheetLoad})`);
        const workspace = this.container.querySelector('#sl-workspace') as HTMLElement;
        const noSelectionMsg = this.container.querySelector('#sl-no-selection-msg') as HTMLElement;
        const sidebarLeft = this.container.querySelector('#sl-sidebar-left') as HTMLElement;
        if (!name) {
            this.clearPreview();
            this.renderTabs([]); // Clear tabs and hide add button
            if (workspace) workspace.style.display = 'none';
            if (noSelectionMsg) noSelectionMsg.style.display = 'flex';
            if (sidebarLeft) sidebarLeft.style.display = 'flex';
            const spriteNameInput = this.container.querySelector('#sl-mgmt-sprite-name') as HTMLInputElement;
            if (spriteNameInput) spriteNameInput.value = '-';
            this.updateManagePanelUI(); // Ensure sheet controls are disabled
            return;
        }
        if (workspace) workspace.style.display = 'flex';
        if (noSelectionMsg) noSelectionMsg.style.display = 'none';
        if (sidebarLeft) sidebarLeft.style.display = 'flex';
        if (!skipSheetLoad) {
            await this.loadSpriteSheets(name);
        }
        if (await this.loadSheetFromMemory(name, this.currentSheet, suppressLoading)) {
            logDev(`[SheetDebug] Loaded from memory: ${this.projectId}:${name}:${this.currentSheet}`);
            this.updateManagePanelUI();
            this.renderTabs(this.parsedSheets);
            this.renderHistoryList();
            this.updateButtonStates();
            return;
        }
        logDev(`[StateDebug] Not in memory, fetching: ${this.projectId}:${name}:${this.currentSheet}`);
        this.isLoading = true;
        const loader = this.container.querySelector('#sl-sheet-loading') as HTMLElement;
        if (loader && !suppressLoading) loader.style.display = 'flex';
        const spriteNameInput = this.container.querySelector('#sl-mgmt-sprite-name') as HTMLInputElement;
        if (spriteNameInput) spriteNameInput.value = name;
        this.updateManagePanelUI();
        const sheet = this.currentSheet;
        this.lastRequestId++;
        const currentRid = this.lastRequestId;

        // --- Guest: try IndexedDB before falling through to API ---
        if (!auth.currentUser) {
            const idbKey = `${this.projectId}:${name}:${sheet}`;
            try {
                const record = await GuestStorageManager.loadSheet(idbKey);
                if (record) {
                    logDev(`[SpriteLab] Guest IDB hit for ${idbKey}`);
                    // Apply metadata to DOM inputs
                    (this.container.querySelector('#sl-meta-width') as HTMLInputElement).value = record.width.toString();
                    (this.container.querySelector('#sl-meta-height') as HTMLInputElement).value = record.height.toString();
                    (this.container.querySelector('#sl-meta-frames') as HTMLInputElement).value = record.frames.toString();
                    this.updateInterval(record.interval);
                    // Load image directly (inline await keeps isLoading=true until canvas is fully set)
                    await new Promise<void>((resolve) => {
                        const img = new Image();
                        img.onload = () => {
                            this.dataCanvas.width = img.width;
                            this.dataCanvas.height = img.height;
                            const dCtx = this.dataCanvas.getContext('2d');
                            if (dCtx) { dCtx.clearRect(0, 0, img.width, img.height); dCtx.drawImage(img, 0, 0); }
                            const canonicalBase64 = this.dataCanvas.toDataURL();
                            const snap = {
                                base64: canonicalBase64,
                                width: record.width, height: record.height,
                                frames: record.frames, orientation: record.orientation,
                                animation: record.animation, interval: record.interval,
                                palette: record.palette || []
                            };
                            this.sessionSheets.set(idbKey, {
                                base64: canonicalBase64, originalBase64: canonicalBase64,
                                width: record.width, height: record.height,
                                frames: record.frames, orientation: record.orientation,
                                animation: record.animation, interval: record.interval,
                                isDirty: false, history: [snap], historyIndex: 0, palette: record.palette || [],
                                baseSnapshot: snap
                            });
                            this.originalBase64 = canonicalBase64;
                            this.currentBase64 = canonicalBase64;
                            resolve();
                        };
                        img.onerror = () => resolve();
                        img.src = record.base64;
                    });
                    if (record.palette && this.colorPicker) {
                        this.colorPicker.setPalette(record.palette);
                    }
                    // isLoading cleared AFTER all canvas state is set — no race window for drawing
                    this.isLoading = false;
                    if (loader) loader.style.display = 'none';
                    await this.updatePreview(this.currentBase64, true);
                    this.updateManagePanelUI();
                    this.renderTabs(this.parsedSheets);
                    this.renderHistoryList();
                    this.updateButtonStates();
                    return;
                }
            } catch (idbErr) {
                logDev(`[SpriteLab] Guest IDB load error for ${idbKey}:`, idbErr);
            }
            // No IDB record — initialize blank canvas inline
            await new Promise<void>((resolve) => {
                const gw = 32, gh = 32, gf = 1, go = 'horizontal';
                const tmpCanvas = document.createElement('canvas');
                tmpCanvas.width = gw; tmpCanvas.height = gh;
                const blankB64 = tmpCanvas.toDataURL();
                const img = new Image();
                img.onload = () => {
                    this.dataCanvas.width = gw;
                    this.dataCanvas.height = gh;
                    const dCtx = this.dataCanvas.getContext('2d');
                    if (dCtx) dCtx.clearRect(0, 0, gw, gh);
                    const defaultPalette = [...ColorPicker.DEFAULT_PALETTE];
                    if (this.colorPicker) {
                        this.colorPicker.setPalette(defaultPalette);
                    }
                    const initialSnap = {
                        base64: blankB64, width: gw, height: gh, frames: gf, orientation: go,
                        animation: 'sequence', interval: 0.2,
                        palette: defaultPalette
                    };
                    this.sessionSheets.set(idbKey, {
                        base64: blankB64, originalBase64: blankB64,
                        width: gw, height: gh, frames: gf, orientation: go,
                        animation: 'sequence', interval: 0.2, isDirty: false,
                        history: [initialSnap], historyIndex: 0,
                        palette: defaultPalette,
                        baseSnapshot: initialSnap
                    });
                    this.originalBase64 = blankB64;
                    this.currentBase64 = blankB64;
                    resolve();
                };
                img.onerror = () => resolve();
                img.src = blankB64;
            });
            this.isLoading = false;
            if (loader) loader.style.display = 'none';
            await this.updatePreview(this.currentBase64, true);
            this.renderHistoryList();
            this.checkDirty();
            this.updateButtonStates();
            return;
        }

        // --- Authenticated: fetch from API ---
        const filename = `${sheet}.png`;
        const apiUrl = (import.meta as any).env.VITE_API_URL ? (import.meta as any).env.VITE_API_URL : '';
        const url = `${apiUrl}/api/sprite-lab/projects/${this.projectId}/sprites/${name}/sheets/${sheet}/${filename}?t=${Date.now()}`;
        logDev(`[SpriteLab] Checking for ${filename} (RID: ${currentRid})...`);
        logDev(`[SheetDebug] checkSpriteAvailability: Name=${name}, Sheet=${sheet}, CurrentKey=${this.projectId}:${name}:${sheet}`);
        try {
            const user = auth.currentUser;
            const idToken = await user?.getIdToken();
            const headers: HeadersInit = {};
            if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
            const res = await fetch(url, { headers });
            if (currentRid !== this.lastRequestId) return;
            let imageFound = false;
            if (res.ok) {
                imageFound = true;
                const blob = await res.blob();
                await new Promise<void>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        if (currentRid !== this.lastRequestId) { resolve(); return; }
                        const base64 = e.target?.result as string;
                        this.currentBase64 = base64;
                        resolve();
                    };
                    reader.readAsDataURL(blob);
                });
            }
            const metaFilename = `${sheet}.ts`;
            const metaUrl = `${apiUrl}/api/sprite-lab/projects/${this.projectId}/sprites/${name}/sheets/${sheet}/${metaFilename}?t=${Date.now()}`;
            const metaRes = await fetch(metaUrl, { headers });
            if (currentRid !== this.lastRequestId) return;
            let width = '32';
            let height = '32';
            let frames = '3';
            let orientation = 'horizontal';
            let animType = 'sequence';
            let finalInterval = 0.20;
            if (metaRes.ok) {
                const text = await metaRes.text();
                const getVal = (key: string, defaultVal: string) => {
                    const regex = new RegExp(`['"]?${key}['"]?\\s*:\\s*(['"]?)(.*?)\\1(?:[\\s,}]|$)`, 'i');
                    const m = text.match(regex);
                    if (m) {
                        const raw = m[2].trim().replace(/[xX]['"]?$/, '').replace(/^['"]|['"]$/g, '');
                        return raw;
                    }
                    return defaultVal;
                };
                const getPalette = () => {
                    const paletteRegex = /palette\s*:\s*\[(.*?)\]/is;
                    const m = text.match(paletteRegex);
                    if (m && m[1]) {
                        const colorsStr = m[1].trim();
                        // Match hex codes like #ffffff or #fff
                        const colorMatches = colorsStr.match(/#([0-9a-fA-F]{3,6})/g);
                        return colorMatches || [];
                    }
                    return null;
                };
                width = getVal('width', '32');
                height = getVal('height', '32');
                frames = getVal('frames', '3');
                orientation = getVal('orientation', 'horizontal');
                const rawInterval = getVal('interval', '');
                finalInterval = (() => {
                    if (!rawInterval) {
                        const speedVal = parseFloat(getVal('animationSpeed', '')) || parseFloat(getVal('speed', '0.2'));
                        return 0.01666 / speedVal;
                    }
                    const val = parseFloat(rawInterval);
                    if (val > 10) return val / 1000;
                    return val;
                })() || 0.20;
                animType = getVal('animationType', 'sequence');
                const loadedPalette = getPalette();
                if (loadedPalette && loadedPalette.length > 0 && this.colorPicker) {
                    this.colorPicker.setPalette(loadedPalette);
                }
            } else {
                logDev(`[SpriteLab] No metadata found (Status: ${metaRes.status}), using defaults.`);
            }
            (this.container.querySelector('#sl-meta-width') as HTMLInputElement).value = width;
            (this.container.querySelector('#sl-meta-height') as HTMLInputElement).value = height;
            (this.container.querySelector('#sl-meta-frames') as HTMLInputElement).value = frames;
            this.updateInterval(finalInterval);
            if (this.currentBase64 && imageFound) {
                logDev(`[SpriteLab] Force refreshing preview with metadata...`);
                // Canonicalize the FileReader blob base64 through a canvas so history/dirty
                // comparisons always operate in the same toDataURL() encoding domain.
                const canonicalBase64 = await new Promise<string>((resolve) => {
                    const tmpImg = new Image();
                    tmpImg.onload = () => {
                        const tmpCanvas = document.createElement('canvas');
                        tmpCanvas.width = tmpImg.naturalWidth;
                        tmpCanvas.height = tmpImg.naturalHeight;
                        const tmpCtx = tmpCanvas.getContext('2d');
                        if (tmpCtx) tmpCtx.drawImage(tmpImg, 0, 0);
                        resolve(tmpCanvas.toDataURL());
                    };
                    tmpImg.onerror = () => resolve(this.currentBase64); // fallback
                    tmpImg.src = this.currentBase64;
                });
                this.currentBase64 = canonicalBase64;
                this.originalBase64 = canonicalBase64;
                await this.updatePreview(canonicalBase64);
                const key = `${this.projectId}:${name}:${sheet}`;
                logDev(`[StateDebug] Image Found. Setting session sheet for ${key}`);
                const initialSnapshot = {
                    base64: canonicalBase64,
                    width: parseInt(width),
                    height: parseInt(height),
                    frames: parseInt(frames),
                    orientation: orientation,
                    animation: animType,
                    interval: finalInterval,
                    palette: [...(this.colorPicker?.getPalette() || [])]
                };
                this.sessionSheets.set(key, {
                    base64: canonicalBase64,
                    originalBase64: canonicalBase64,
                    width: parseInt(width),
                    height: parseInt(height),
                    frames: parseInt(frames),
                    orientation: orientation,
                    animation: animType,
                    interval: finalInterval,
                    isDirty: false,
                    history: [initialSnapshot],
                    historyIndex: 0,
                    palette: [...(this.colorPicker?.getPalette() || [])],
                    baseSnapshot: initialSnapshot
                });
                this.renderHistoryList();
            } else {
                logDev(`[StateDebug] Image Missing. Initializing BLANK sheet for ${name}:${sheet}`);
                const gw = parseInt(width) || 32;
                const gh = parseInt(height) || 32;
                const gf = parseInt(frames) || 1;
                const go = orientation;
                const targetW = go === 'horizontal' ? gw * gf : gw;
                const targetH = go === 'horizontal' ? gh : gh * gf;
                logDev(`[SpriteLab] Image not found. Initializing blank ${targetW}x${targetH} canvas.`);
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = targetW;
                tempCanvas.height = targetH;
                const blankB64 = tempCanvas.toDataURL();
                const ctx = this.dataCanvas.getContext('2d');
                if (ctx) ctx.clearRect(0, 0, this.dataCanvas.width, this.dataCanvas.height);
                await this.updatePreview(blankB64);
                this.currentBase64 = blankB64;
                this.originalBase64 = blankB64;
                const defaultPalette = [...ColorPicker.DEFAULT_PALETTE];
                if (this.colorPicker) {
                    this.colorPicker.setPalette(defaultPalette);
                }
                const key = `${this.projectId}:${name}:${sheet}`;
                this.sessionSheets.set(key, {
                    base64: blankB64,
                    originalBase64: blankB64,
                    width: gw, height: gh, frames: gf, orientation: go,
                    animation: animType,
                    interval: finalInterval,
                    isDirty: false,
                    history: [{
                        base64: blankB64,
                        width: gw, height: gh, frames: gf, orientation: go,
                        animation: animType,
                        interval: finalInterval,
                        palette: defaultPalette
                    }],
                    historyIndex: 0,
                    palette: defaultPalette,
                    baseSnapshot: {
                        base64: blankB64,
                        width: gw, height: gh, frames: gf, orientation: go,
                        animation: animType,
                        interval: finalInterval,
                        palette: defaultPalette
                    }
                });
                this.renderHistoryList();
            }
            this.isLoading = false;
            if (loader) loader.style.display = 'none';
            this.checkDirty();
            this.updateButtonStates();
        } catch (e) {
            logDev(`[SpriteLab] Check failed: ${e}`);
            this.isLoading = false;
            if (loader) loader.style.display = 'none';
            if (currentRid === this.lastRequestId) {
                this.clearPreview();
            }
        }
    }
    public hasContent(): boolean {
        const ctx = this.dataCanvas.getContext('2d');
        if (!ctx) return false;
        const w = this.dataCanvas.width;
        const h = this.dataCanvas.height;
        if (w === 0 || h === 0) return false;
        const imgData = ctx.getImageData(0, 0, w, h).data;
        for (let i = 3; i < imgData.length; i += 4) {
            if (imgData[i] > 0) {
                return true;
            }
        }
        return false;
    }

    public checkDirty(): boolean {
        if (!this.currentBase64) return false;
        const key = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
        const sheetData = this.sessionSheets.get(key);
        if (!this.originalBase64) {
            const contentExists = this.hasContent();
            if (sheetData) sheetData.isDirty = contentExists;
            return contentExists;
        }
        let isDirty = this.currentBase64 !== this.originalBase64;
        if (sheetData && sheetData.history && sheetData.history.length > 0) {
            const first = sheetData.baseSnapshot || sheetData.history[0];
            const current = sheetData.history[sheetData.historyIndex];
            const sameMetadata =
                first.width === current.width &&
                first.height === current.height &&
                first.frames === current.frames &&
                first.orientation === current.orientation &&
                first.animation === sheetData.animation &&
                first.interval === sheetData.interval;
            const sameImage = first.base64 === current.base64;
             // Compare current picker/memory palette with first snapshot's palette
            const currentPalette = this.colorPicker?.getPalette() || (sheetData && sheetData.palette) || [];
            const samePalette = JSON.stringify(first.palette) === JSON.stringify(currentPalette);

            if (sameMetadata && sameImage && samePalette) {
                isDirty = false;
            } else {
                isDirty = true;
            }
        }
        if (sheetData) sheetData.isDirty = isDirty;
        return isDirty;
    }
    public checkAnyDirty(): boolean {
        this.checkDirty();
        for (const sheet of this.sessionSheets.values()) {
            if (sheet.isDirty) return true;
        }
        return false;
    }
    public updateButtonStates() {
        const saveBtn = this.container.querySelector('#sl-save-btn') as HTMLButtonElement;
        const saveAllBtn = this.container.querySelector('#sl-save-all-btn') as HTMLButtonElement;
        const isCurrentDirty = this.checkDirty();
        if (isCurrentDirty) {
            saveBtn.disabled = false;
            saveBtn.style.opacity = '1';
            saveBtn.style.cursor = 'pointer';
        } else {
            saveBtn.disabled = true;
            saveBtn.style.opacity = '0.5';
            saveBtn.style.cursor = 'not-allowed';
        }
        let anyDirty = isCurrentDirty;
        if (!anyDirty) {
            this.sessionSheets.forEach(s => {
                if (s.isDirty) anyDirty = true;
            });
        }
        if (anyDirty) {
            saveAllBtn.disabled = false;
            saveAllBtn.style.opacity = '1';
            saveAllBtn.style.cursor = 'pointer';
        } else {
            saveAllBtn.disabled = true;
            saveAllBtn.style.opacity = '0.5';
            saveAllBtn.style.cursor = 'not-allowed';
        }
    }
    public updateInterval(v: number) {
        if (isNaN(v)) return;
        v = Math.max(0.01, Math.min(2.5, v));
        const key = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
        const sheetData = this.sessionSheets.get(key);
        if (sheetData) {
            sheetData.interval = v;
        }
        if (this.currentAnim) {
            this.currentAnim.animationSpeed = 0.01666 / v;
        }
    }

    public updateAnimation(animType: 'sequence' | 'bounce' | 'random') {
        const key = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
        const sheetData = this.sessionSheets.get(key);
        if (sheetData) {
            sheetData.animation = animType;
            if (this.currentBase64) {
                // Re-create the preview with the new animation logic
                this.updatePreview(this.currentBase64, false);
            }
        }
    }
    public updateSliderFill(input: HTMLInputElement) {
        const val = (parseFloat(input.value) - parseFloat(input.min)) / (parseFloat(input.max) - parseFloat(input.min)) * 100;
        input.style.background = `linear-gradient(to right, #a855f7 0%, #a855f7 ${val}%, #ffffff ${val}%, #ffffff 100%)`;
    }
    private clearPreview() {
        this.currentBase64 = '';
        const frame = this.container.querySelector('#sl-sheet-frame') as HTMLDivElement;
        frame.style.display = 'none';
        if (this.currentAnim) {
            this.currentAnim.destroy();
            this.currentAnim = null;
        }
        this.spriteContainer?.removeChildren();
    }
        public async updatePreview(base64: string, resetTransform: boolean = true): Promise<void> {
        this.currentBase64 = base64;
        const canvas = this.container.querySelector('#sl-sheet-canvas') as HTMLCanvasElement;
        const frame = this.container.querySelector('#sl-sheet-frame') as HTMLDivElement;
        const container = this.container.querySelector('#sl-sheet-container') as HTMLDivElement;
        const info = this.container.querySelector('#sl-sheet-info') as HTMLDivElement;
        const wInput = this.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fInput = this.container.querySelector('#sl-meta-frames') as HTMLInputElement;
        const scaleWInput = this.container.querySelector('#sl-scale-width') as HTMLInputElement;
        const scaleHInput = this.container.querySelector('#sl-scale-height') as HTMLInputElement;
        const frameW = parseInt(wInput.value) || 32;
        const frameH = parseInt(hInput.value) || 32;
        const frameCount = parseInt(fInput.value) || 3;

        if (scaleWInput) scaleWInput.value = frameW.toString();
        if (scaleHInput) scaleHInput.value = frameH.toString();

        return new Promise((resolve) => {
            const img = new Image();
            img.onload = async () => {
                const isHorz = img.width >= img.height;
                const targetW = isHorz ? frameW * frameCount : frameW;
                const targetH = isHorz ? frameH : frameH * frameCount;
                const dW = Math.max(img.naturalWidth, targetW);
                const dH = Math.max(img.naturalHeight, targetH);
                this.dataCanvas.width = dW;
                this.dataCanvas.height = dH;
                const dCtx = this.dataCanvas.getContext('2d');
                if (dCtx) {
                    dCtx.clearRect(0, 0, dW, dH);
                    dCtx.drawImage(img, 0, 0);
                }
                const texW = dW;
                const texH = dH;
                canvas.width = texW;
                canvas.height = texH;
                frame.setAttribute('data-w', targetW.toString());
                frame.setAttribute('data-h', targetH.toString());
                frame.style.overflow = 'hidden';
                canvas.style.width = '100%';
                canvas.style.height = '100%';
                const svg = this.container.querySelector('#sl-sheet-grid') as SVGSVGElement;
                if (svg) {
                    svg.setAttribute('viewBox', `0 0 ${targetW} ${targetH}`);
                    svg.setAttribute('preserveAspectRatio', 'none');
                    svg.style.width = '100%';
                    svg.style.height = '100%';
                    svg.style.pointerEvents = 'none';
                }
                const cW = container.clientWidth - 40;
                const cH = container.clientHeight - 40;
                let fitScale = Math.min(cW / targetW, cH / targetH);
                this.defaultScale = fitScale;
                if (resetTransform) {
                    this.transformState.scale = fitScale;
                    this.transformState.pX = 0;
                    this.transformState.pY = 0;
                    if (this.selectedFrame) {
                        if (this.selectedFrame.col >= Math.max(1, isHorz ? frameCount : 1)) this.selectedFrame.col = 0;
                        if (this.selectedFrame.row >= Math.max(1, isHorz ? 1 : frameCount)) this.selectedFrame.row = 0;
                    }
                } else {
                    const cols = isHorz ? frameCount : 1;
                    const rows = isHorz ? 1 : frameCount;
                    if (this.selectedFrame) {
                        if (this.selectedFrame.col >= cols) this.selectedFrame.col = cols - 1;
                        if (this.selectedFrame.row >= rows) this.selectedFrame.row = rows - 1;
                        if (this.selectedFrame.col < 0) this.selectedFrame.col = 0;
                        if (this.selectedFrame.row < 0) this.selectedFrame.row = 0;
                    }
                }
                if (this.updateSheetTransform) this.updateSheetTransform();
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.imageSmoothingEnabled = false;
                    ctx.clearRect(0, 0, texW, texH);
                    ctx.drawImage(img, 0, 0);
                }
                frame.style.display = 'block';
                if (info) {
                    info.style.display = 'none';
                }
                this.updateGridOverlay(frameW, frameH, targetW, targetH);
                this.updateSelectionHighlight(frameW, frameH);
                if (this.toolState.grid.active) {
                    this.editorManager.updateFineGrid();
                }
                this.editorManager.setupSpriteEditor(canvas, this.dataCanvas, frameW, frameH, targetW, targetH);
                const texture = PIXI.Texture.from(img);
                texture.baseTexture.scaleMode = PIXI.SCALE_MODES.NEAREST;
                this.createAnim(texture);
                this.updateButtonStates();
                resolve();
            };
            img.src = base64;
        });
    }
    private async createAnim(texture: PIXI.Texture) {
        if (this.currentAnim) {
            this.currentAnim.destroy();
            this.currentAnim = null;
        }
        this.spriteContainer?.removeChildren();
        const wInput = this.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fInput = this.container.querySelector('#sl-meta-frames') as HTMLInputElement;
        const frameW = parseInt(wInput.value) || 32;
        const frameH = parseInt(hInput.value) || 32;
        const frameCount = parseInt(fInput.value) || 3;
        
        const isHorz = texture.width >= texture.height;
        const key = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
        const sheetData = this.sessionSheets.get(key);
        const animType = sheetData?.animation || 'sequence';
        const interval = sheetData?.interval || 1.0;
        const baseFrames: PIXI.Texture[] = [];
        console.log(`[SpriteLab] Creating Anim.Texture: ${texture.width}x${texture.height}, Config: ${frameW}x${frameH}, Frames: ${frameCount}, Orient: ${isHorz ? 'horizontal' : 'vertical'} `);
        if (frameW > texture.width || frameH > texture.height) {
            console.warn("[SpriteLab] Frame size larger than texture dimensions");
        }
        if (isHorz) {
            for (let i = 0; i < frameCount; i++) {
                const x = i * frameW;
                if (x < texture.width) {
                    const w = Math.min(frameW, texture.width - x);
                    const h = Math.min(frameH, texture.height);
                    baseFrames.push(new PIXI.Texture(texture.baseTexture, new PIXI.Rectangle(x, 0, w, h)));
                }
            }
        } else {
            for (let i = 0; i < frameCount; i++) {
                const y = i * frameH;
                if (y < texture.height) {
                    const w = Math.min(frameW, texture.width);
                    const h = Math.min(frameH, texture.height - y);
                    baseFrames.push(new PIXI.Texture(texture.baseTexture, new PIXI.Rectangle(0, y, w, h)));
                }
            }
        }
        if (baseFrames.length === 0) {
            console.error("[SpriteLab] No frames generated! Check config vs image size.");
            alert(this.t('err_extract_frames'));
            return;
        }
        let finalFrames: PIXI.Texture[] = [];
        if (animType === 'bounce') {
            finalFrames = [...baseFrames];
            for (let i = baseFrames.length - 2; i > 0; i--) {
                finalFrames.push(baseFrames[i]);
            }
        } else if (animType === 'random') {
            const targetFrames = baseFrames.length * 10;
            for (let i = 0; i < targetFrames; i++) {
                const rndIndex = Math.floor(Math.random() * baseFrames.length);
                finalFrames.push(baseFrames[rndIndex]);
            }
        } else {
            finalFrames = [...baseFrames];
        }
        this.currentAnim = new PIXI.AnimatedSprite(finalFrames);
        this.currentAnim.animationSpeed = 0.01666 / interval;
        this.currentAnim.width = frameW * (64 / frameW); // Scale to fit 64? No, that distorts.
        this.currentAnim.width = frameW;
        this.currentAnim.height = frameH;
        this.currentAnim.anchor.set(0.5, 0.5); // Center anchor for flat view? Or Bottom Center? 
        this.currentAnim.anchor.set(0.5, 0.5);
        this.currentAnim.x = 0;
        this.currentAnim.y = 0;
        const slider = this.container.querySelector('#sl-interval-slider') as HTMLInputElement;
        if (slider) {
            this.currentAnim.animationSpeed = 0.01666 / parseFloat(slider.value);
        }
        this.currentAnim.play();
        this.spriteContainer?.addChild(this.currentAnim);
        this.updateSpritePosition();
        this.editorManager.resizeHandle();
    }
    private updateSpritePosition() {
        this.previewApp?.stage.pivot.set(0, 0);
        if (this.currentAnim) {
            this.currentAnim.x = 0;
            this.currentAnim.y = 0;
            this.currentAnim.zIndex = 0;
        }
    }
    private updateGridOverlay(frameW: number, frameH: number, texW: number, texH: number) {
        const svg = this.container.querySelector('#sl-sheet-grid') as SVGSVGElement;
        if (!svg) return;
        while (svg.firstChild) {
            svg.removeChild(svg.firstChild);
        }
        let i = 1;
        while (true) {
            const x = i * frameW;
            if (x >= texW) break; // texW is the Active Width here
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', `${x}`);
            line.setAttribute('y1', '0');
            line.setAttribute('x2', `${x}`);
            line.setAttribute('y2', '100%');
            line.setAttribute('stroke', '#f85149');
            line.setAttribute('stroke-width', '1');
            line.setAttribute('vector-effect', 'non-scaling-stroke');
            line.setAttribute('stroke-dasharray', '8, 8');
            svg.appendChild(line);
            i++;
        }
        let j = 1;
        while (true) {
            const y = j * frameH;
            if (y >= texH) break;
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', '0');
            line.setAttribute('y1', `${y}`);
            line.setAttribute('x2', '100%');
            line.setAttribute('y2', `${y}`);
            line.setAttribute('stroke', '#f85149');
            line.setAttribute('stroke-width', '1');
            line.setAttribute('vector-effect', 'non-scaling-stroke');
            line.setAttribute('stroke-dasharray', '8, 8');
            svg.appendChild(line);
            j++;
        }
            const border = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        border.setAttribute('x', '0');
        border.setAttribute('y', '0');
        border.setAttribute('width', `${texW}`);
        border.setAttribute('height', `${texH}`);
        border.setAttribute('fill', 'none');
        border.setAttribute('stroke', '#3c3c3c'); // Match original border color
        border.style.strokeWidth = 'calc(2px * var(--sl-inverse-scale, 1))'; // Increased from 1px
        border.style.pointerEvents = 'none';
        svg.appendChild(border);
    }
    public getLangButton(): HTMLButtonElement | null {
        return this.container.querySelector('#sl-lang-btn') as HTMLButtonElement | null;
    }
    public updateLangText(text: string) {
        const textSpan = this.container.querySelector('#sl-lang-text');
        if (textSpan) textSpan.textContent = text;
    }
    async handleCreateSprite() {
        const input = this.container.querySelector('#sl-new-sprite-name') as HTMLInputElement;
        const name = input.value.trim();
        if (!name) return alert(this.t('err_name_empty'));
        try {
            const user = auth.currentUser;

            if (!user) {
                // Guest: add sprite locally
                const proj = this.projectTree.find(p => p.name === this.projectId);
                if (proj) {
                    if (proj.sprites.includes(name)) return alert(this.t('err_sprite_exists'));
                    proj.sprites.push(name);
                } else {
                    this.projectTree.push({ name: this.projectId, sprites: [name] });
                    if (!this.projects.includes(this.projectId)) this.projects.push(this.projectId);
                }
                input.value = '';
                (this.container.querySelector('#sl-modal-create-sprite') as HTMLElement).style.display = 'none';
                this.itemId = name;
                this.renderExplorer();
                this.checkSpriteAvailability();
                return;
            }

            const idToken = await user.getIdToken();
            const apiUrl = (import.meta as any).env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/api/sprite-lab/projects/${encodeURIComponent(this.projectId)}/sprites`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken, name })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to create sprite');
            }
            const data = await res.json();
            input.value = '';
            (this.container.querySelector('#sl-modal-create-sprite') as HTMLElement).style.display = 'none';
            await this.loadSpriteList();
            this.itemId = data.name;
            this.renderExplorer(); // Ensure highlight
            this.checkSpriteAvailability(); // Load it
        } catch (e: any) {
            alert(e.message);
        }
    }
    async handleAddSheet() {
        const input = this.container.querySelector('#sl-new-sheet-name') as HTMLInputElement;
        const sheetName = input.value.trim();
        const spriteName = this.itemId;
        if (!sheetName || !spriteName) return;
        try {
            const user = auth.currentUser;

            if (!user) {
                // Guest: register sheet locally (a blank IDB entry for the new sheet)
                if (!this.parsedSheets.includes(sheetName)) {
                    this.parsedSheets.push(sheetName);
                }
                input.value = '';
                (this.container.querySelector('#sl-modal-add-sheet') as HTMLElement).style.display = 'none';
                this.currentSheet = sheetName;
                this.renderTabs(this.parsedSheets);
                this.checkSpriteAvailability(true); // Will create blank canvas + IDB entry on save
                return;
            }

            const idToken = await user.getIdToken();
            const apiUrl = (import.meta as any).env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/api/sprite-lab/projects/${this.projectId}/sprites/${spriteName}/sheets/${sheetName}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to add sheet');
            }
            input.value = '';
            (this.container.querySelector('#sl-modal-add-sheet') as HTMLElement).style.display = 'none';
            await this.loadSpriteSheets(spriteName);
            this.currentSheet = sheetName;
            this.renderTabs(this.parsedSheets); // Refresh UI
            this.checkSpriteAvailability(true); // Load image
        } catch (e: any) {
            alert(e.message);
        }
    }
    async handleRenameProject(oldName: string, newName: string): Promise<string | null> {
        if (!oldName || !newName || oldName === newName) return null;
        if (!auth.currentUser) {
            // Guest: rename in-memory + migrate IDB keys
            try {
                await GuestStorageManager.renameProjectKeys(oldName, newName);
            } catch (e) {
                console.error('[SpriteLab] IDB renameProject failed:', e);
            }
            // Update in-memory tree
            const proj = this.projectTree.find(p => p.name === oldName);
            if (proj) proj.name = newName;
            const idx = this.projects.indexOf(oldName);
            if (idx >= 0) this.projects[idx] = newName;
            // Migrate sessionSheets keys
            const prefix = `${oldName}:`;
            const oldEntries = Array.from(this.sessionSheets.entries()).filter(([k]) => k.startsWith(prefix));
            for (const [oldKey, data] of oldEntries) {
                const newKey = newName + ':' + oldKey.slice(prefix.length);
                this.sessionSheets.set(newKey, data);
                this.sessionSheets.delete(oldKey);
            }
            if (this.projectId === oldName) this.projectId = newName;
            if (this.expandedProjects.has(oldName)) {
                this.expandedProjects.delete(oldName);
                this.expandedProjects.add(newName);
            }
            this.renderExplorer();
            return newName;
        }
        const result = await this.apiManager.renameProject(oldName, newName);
        if (result) {
            if (this.projectId === oldName) this.projectId = result;
            if (this.expandedProjects.has(oldName)) {
                this.expandedProjects.delete(oldName);
                this.expandedProjects.add(result);
            }
            await this.loadProjectList();
        }
        return result;
    }
    async handleRenameSheet() {
        const input = this.container.querySelector('#sl-rename-sheet-input') as HTMLInputElement;
        const newSheetName = input.value.trim();
        const oldSheet = this.currentSheet;
        if (!newSheetName || !this.itemId || newSheetName === oldSheet) return;
        if (!auth.currentUser) {
            // Guest: rename in-memory + migrate IDB key
            try {
                await GuestStorageManager.renameSheetKeys(this.projectId, this.itemId, oldSheet, newSheetName);
            } catch (e) {
                console.error('[SpriteLab] IDB renameSheet failed:', e);
            }
            const oldKey = `${this.projectId}:${this.itemId}:${oldSheet}`;
            const newKey = `${this.projectId}:${this.itemId}:${newSheetName}`;
            if (this.sessionSheets.has(oldKey)) {
                this.sessionSheets.set(newKey, this.sessionSheets.get(oldKey)!);
                this.sessionSheets.delete(oldKey);
            }
            const i = this.parsedSheets.indexOf(oldSheet);
            if (i >= 0) this.parsedSheets[i] = newSheetName;
            this.currentSheet = newSheetName;
            this.renderTabs(this.parsedSheets);
            input.value = '';
            (this.container.querySelector('#sl-modal-rename-sheet') as HTMLElement).style.display = 'none';
            return;
        }
        const success = await this.apiManager.renameSheet(this.projectId, this.itemId, oldSheet, newSheetName);
        if (success) {
            this.currentSheet = newSheetName;
            await this.loadSpriteSheets(this.itemId);
            input.value = '';
            (this.container.querySelector('#sl-modal-rename-sheet') as HTMLElement).style.display = 'none';
        }
    }
    async handleRenameSprite(oldNameParam?: string) {
        const oldName = oldNameParam || this.itemId;
        if (!oldName) return;
        const newName = prompt(`Rename sprite "${oldName}" to:`, oldName);
        if (newName && newName !== oldName) {
            const success = await this.handleRenameSpriteInternal(oldName, newName, this.projectId);
            if (success) {
                // handleRenameSpriteInternal already calls loadProjectList and sets itemId
                this.checkSpriteAvailability();
            }
        }
    }

    public async copySheet(projectId: string, spriteName: string, sheetName: string) {
        logDev(`[SpriteLab] copySheet: ${projectId}:${spriteName}:${sheetName}`);
        const sourceKey = `${projectId}:${spriteName}:${sheetName}`;
        let sheetData = this.sessionSheets.get(sourceKey);

        if (!sheetData) {
            // If not in memory, we might need to load it first or get from IDB/API
            await this.loadSheetFromMemory(spriteName, sheetName, true);
            sheetData = this.sessionSheets.get(sourceKey);
        }

        if (!sheetData) {
            logDev(`[SpriteLab] copySheet: source sheet data not found for ${sourceKey}`);
            return;
        }

        const newSheetName = `${sheetName} - Copy`;

        // Ensure unique name
        let finalName = newSheetName;
        let counter = 1;
        while (this.parsedSheets.includes(finalName)) {
            finalName = `${newSheetName} ${++counter}`;
        }

        const newKey = `${projectId}:${spriteName}:${finalName}`;
        const newSheetData = JSON.parse(JSON.stringify(sheetData));
        newSheetData.isDirty = true;
        
        // Reset history/undo stack and context for the copy
        if (newSheetData.history && newSheetData.historyIndex !== undefined) {
            const currentSnapshot = newSheetData.history[newSheetData.historyIndex];
            newSheetData.history = [JSON.parse(JSON.stringify(currentSnapshot))];
            newSheetData.historyIndex = 0;
            newSheetData.baseSnapshot = JSON.parse(JSON.stringify(currentSnapshot));
        }
        newSheetData.originalBase64 = newSheetData.base64;

        this.sessionSheets.set(newKey, newSheetData);
        if (!this.parsedSheets.includes(finalName)) {
            this.parsedSheets.push(finalName);
        }

        // If guest, save to IDB immediately
        if (!auth.currentUser) {
            await GuestStorageManager.saveSheet({
                ...newSheetData,
                key: newKey,
                savedAt: Date.now()
            });
        }

        this.currentSheet = finalName;
        this.renderTabs(this.parsedSheets);
        await this.loadSheetFromMemory(spriteName, finalName);
        this.checkDirty();
        this.updateButtonStates();
        logDev(`[SpriteLab] copySheet: Created ${finalName}`);
    }

    public async copySprite(sourceProjectId: string, spriteName: string, targetProjectId: string) {
        logDev(`[SpriteLab] copySprite: ${sourceProjectId}:${spriteName} -> ${targetProjectId}`);
        
        // 1. Get sheets for the sprite
        let sheets: string[] = [];
        if (!auth.currentUser) {
            sheets = await GuestStorageManager.getSheetsForSprite(sourceProjectId, spriteName);
        } else {
            const apiUrl = (import.meta as any).env.VITE_API_URL || '';
            const user = auth.currentUser;
            const idToken = await user.getIdToken();
            const res = await fetch(`${apiUrl}/api/sprite-lab/projects/${sourceProjectId}/sprites/${spriteName}/sheets`, {
                headers: { 'Authorization': `Bearer ${idToken}` }
            });
            if (res.ok) {
                const data = await res.json();
                sheets = data.map((s: any) => (typeof s === 'string' ? s : s.name));
            }
        }

        if (sheets.length === 0) sheets = ['main'];

        // 2. Determine target sprite name
        let targetSpriteName = spriteName;
        const targetProj = this.projectTree.find(p => p.name === targetProjectId);
        if (targetProj) {
            if (targetProj.sprites.includes(targetSpriteName)) {
                const baseName = `${spriteName} - Copy`;
                let counter = 1;
                let finalName = baseName;
                while (targetProj.sprites.includes(finalName)) {
                    finalName = `${baseName} ${++counter}`;
                }
                targetSpriteName = finalName;
            }
        }

        // 3. Copy each sheet
        for (const sheetName of sheets) {
            const sourceKey = `${sourceProjectId}:${spriteName}:${sheetName}`;
            
            // Load source data
            let sourceData: any = this.sessionSheets.get(sourceKey);
            if (!sourceData) {
                if (!auth.currentUser) {
                    sourceData = await GuestStorageManager.loadSheet(sourceKey);
                } else {
                    // This is more complex for API, might need a dedicated copy endpoint
                    // For now, let's try to load it into memory if authenticated
                    await this.loadSheetFromMemory(spriteName, sheetName, true);
                    sourceData = this.sessionSheets.get(sourceKey);
                }
            }

            if (sourceData) {
                const targetKey = `${targetProjectId}:${targetSpriteName}:${sheetName}`;
                const newSheetData = JSON.parse(JSON.stringify(sourceData));
                newSheetData.isDirty = true;

                // Reset history/undo stack and context for the copy
                if (newSheetData.history && newSheetData.historyIndex !== undefined) {
                    const currentSnapshot = newSheetData.history[newSheetData.historyIndex];
                    newSheetData.history = [JSON.parse(JSON.stringify(currentSnapshot))];
                    newSheetData.historyIndex = 0;
                    newSheetData.baseSnapshot = JSON.parse(JSON.stringify(currentSnapshot));
                }
                newSheetData.originalBase64 = newSheetData.base64;

                this.sessionSheets.set(targetKey, newSheetData);
                
                // If guest, save to IDB immediately to ensure it persists
                if (!auth.currentUser) {
                    await GuestStorageManager.saveSheet({
                        ...newSheetData,
                        key: targetKey,
                        savedAt: Date.now()
                    });
                }
            }
        }

        // 4. Refresh UI
        if (targetProj) {
            targetProj.sprites.push(targetSpriteName);
        } else {
            const now = new Date().toISOString();
            this.projectTree.push({ 
                name: targetProjectId, 
                sprites: [targetSpriteName],
                createdAt: now,
                updatedAt: now
            });
        }
        
        if (!this.projects.includes(targetProjectId)) {
            this.projects.push(targetProjectId);
        }

        this.projectId = targetProjectId;
        this.itemId = targetSpriteName;
        this.currentSheet = sheets[0] || 'main';
        
        this.renderExplorer();
        this.checkSpriteAvailability();
        logDev(`[SpriteLab] copySprite: Created ${targetSpriteName} in ${targetProjectId}`);
    }

    public async copyProject(sourceProjectId: string) {
        logDev(`[SpriteLab] copyProject: ${sourceProjectId}`);
        
        let targetProjectId = `${sourceProjectId} - Copy`;
        let counter = 1;
        let finalName = targetProjectId;
        while (this.projects.includes(finalName)) {
            finalName = `${targetProjectId} ${++counter}`;
        }
        targetProjectId = finalName;

        const sourceProj = this.projectTree.find(p => p.name === sourceProjectId);
        if (!sourceProj) return;

        // Copy each sprite in the project
        for (const spriteName of sourceProj.sprites) {
            await this.copySprite(sourceProjectId, spriteName, targetProjectId);
        }

        logDev(`[SpriteLab] copyProject: Created ${targetProjectId}`);
    }
    public async handleSave() {
        this.colorPicker?.close();
        logDev(`[SpriteLab] handleSave: ${this.projectId}:${this.itemId}:${this.currentSheet}`);
        const spriteName = this.itemId;
        const sheetName = this.currentSheet;
        if (!spriteName || !sheetName) return alert(this.t('err_select_sprite'));
        if (!this.currentBase64) return alert(this.t('err_no_content'));
        const wInput = this.container.querySelector('#sl-meta-width') as HTMLInputElement;
        const hInput = this.container.querySelector('#sl-meta-height') as HTMLInputElement;
        const fInput = this.container.querySelector('#sl-meta-frames') as HTMLInputElement;
        const key = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
        const sheetData = this.sessionSheets.get(key);
        const saveBtn = this.container.querySelector('#sl-save-btn') as HTMLButtonElement;
        const originalText = saveBtn.innerHTML;
        saveBtn.innerText = this.t('msg_saving');
        saveBtn.disabled = true;

        // --- Guest (not logged in): save to IndexedDB ---
        if (!auth.currentUser) {
            try {
                const targetKey = `${this.projectId}:${spriteName}:${sheetName}`;
                await GuestStorageManager.saveSheet({
                    key: targetKey,
                    base64: this.currentBase64,
                    originalBase64: this.currentBase64,
                    width: wInput ? parseInt(wInput.value) || 32 : 32,
                    height: hInput ? parseInt(hInput.value) || 32 : 32,
                    frames: fInput ? parseInt(fInput.value) || 1 : 1,
                    animation: sheetData?.animation || 'sequence',
                    interval: sheetData?.interval || 0.2,
                    palette: [...(this.colorPicker?.getPalette() || [])],
                    savedAt: Date.now()
                });
                saveBtn.disabled = false;
                saveBtn.innerHTML = `<svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg> ${this.t('action_saved')}`;
                setTimeout(() => saveBtn.innerHTML = originalText, 2000);
                this.originalBase64 = this.currentBase64;
                const targetSheetData = this.sessionSheets.get(targetKey);
                if (targetSheetData) {
                    targetSheetData.originalBase64 = this.currentBase64;
                    if (targetSheetData.history) {
                        targetSheetData.baseSnapshot = { 
                            ...targetSheetData.history[targetSheetData.historyIndex],
                            animation: targetSheetData.animation,
                            interval: targetSheetData.interval,
                            palette: [...(this.colorPicker?.getPalette() || targetSheetData.palette || [])]
                        };
                    }
                    targetSheetData.isDirty = false;
                }
                this.saveSheetToMemory();
                this.checkDirty();
                this.updateButtonStates();
            } catch (e) {
                saveBtn.disabled = false;
                saveBtn.innerHTML = originalText;
                console.error('[SpriteLab] IndexedDB save failed:', e);
                alert(this.t('err_indexeddb'));
            }
            return;
        }

        // --- Authenticated user: save to API ---
        const metadata = {
            width: parseInt(wInput.value) || 32,
            height: parseInt(hInput.value) || 32,
            frames: parseInt(fInput.value) || 3,
            animationType: sheetData?.animation || 'sequence',
            interval: sheetData?.interval || 1.0,
            palette: [...(this.colorPicker?.getPalette() || [])]
        };
        const result = await this.apiManager.uploadSprite(this.projectId, spriteName, sheetName, this.currentBase64, metadata);
        saveBtn.disabled = false;
        if (result.success) {
            saveBtn.innerHTML = `<svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg> ${this.t('action_saved')}`;
            setTimeout(() => saveBtn.innerHTML = originalText, 2000);
            this.originalBase64 = this.currentBase64;
            const key = `${this.projectId}:${spriteName}:${sheetName}`;
            const sheetData = this.sessionSheets.get(key);
            if (sheetData) {
                sheetData.originalBase64 = this.currentBase64;
                if (sheetData.history) {
                    sheetData.baseSnapshot = { 
                        ...sheetData.history[sheetData.historyIndex],
                        animation: sheetData.animation,
                        interval: sheetData.interval,
                        palette: [...(this.colorPicker?.getPalette() || sheetData.palette || [])]
                    };
                }
                sheetData.isDirty = false;
            }
            this.saveSheetToMemory(); // Update memory
            this.checkDirty();
            this.updateButtonStates();
        } else {
            saveBtn.innerHTML = originalText;
            alert(result.error);
        }
    }
    async handleSaveAll() {
        this.colorPicker?.close();
        this.saveSheetToMemory();
        const dirtySheets: { key: string, project: string, sprite: string, sheet: string }[] = [];
        this.sessionSheets.forEach((data, key) => {
            if (data.isDirty) {
                const parts = key.split(':');
                if (parts.length === 3) {
                    dirtySheets.push({ key, project: parts[0], sprite: parts[1], sheet: parts[2] });
                }
            }
        });

        if (dirtySheets.length === 0) {
            alert(this.t('msg_no_unsaved_sheets'));
            return;
        }

        const projectsMap = new Map<string, typeof dirtySheets>();
        for (const item of dirtySheets) {
            if (!projectsMap.has(item.project)) {
                projectsMap.set(item.project, []);
            }
            projectsMap.get(item.project)!.push(item);
        }

        const list = this.container.querySelector('#sl-save-all-list') as HTMLDivElement;
        list.innerHTML = '';
        const sortedProjects = Array.from(projectsMap.keys()).sort();
        sortedProjects.forEach(proj => {
            const header = document.createElement('h3');
            header.innerText = proj === 'project' ? 'Project' : `Project: ${proj}`;
            header.style.color = '#fff';
            header.style.marginBottom = '8px';
            header.style.fontSize = '14px';
            list.appendChild(header);

            projectsMap.get(proj)!.forEach(item => {
                const wrapper = document.createElement('div');
                wrapper.className = 'sl-layout-36';
                const checkbox = document.createElement('input');
                checkbox.type = 'checkbox';
                checkbox.className = 'sl-checkbox';
                checkbox.checked = true;
                checkbox.dataset.key = item.key;
                checkbox.id = `sl-save-check-${item.key}`;
                const label = document.createElement('label');
                label.htmlFor = `sl-save-check-${item.key}`;
                label.innerText = `${item.sprite} / ${item.sheet}`;
                label.style.color = '#cccccc';
                wrapper.appendChild(checkbox);
                wrapper.appendChild(label);
                list.appendChild(wrapper);
            });
        });

        const modal = this.container.querySelector('#sl-modal-save-all') as HTMLElement;
        modal.style.display = 'flex';

        const confirmBtn = this.container.querySelector('#sl-save-all-confirm') as HTMLButtonElement;
        const cancelBtn = this.container.querySelector('#sl-save-all-cancel') as HTMLButtonElement;
        const newConfirm = confirmBtn.cloneNode(true);
        confirmBtn.parentNode?.replaceChild(newConfirm, confirmBtn);

        cancelBtn.onclick = () => {
            modal.style.display = 'none';
        };

        newConfirm.addEventListener('click', async () => {
            const checks = list.querySelectorAll('input[type="checkbox"]:checked');
            const keysToSave = Array.from(checks).map(c => (c as HTMLInputElement).dataset.key!);

            if (keysToSave.length === 0) return;

            (newConfirm as HTMLButtonElement).innerText = this.t('msg_saving');
            (newConfirm as HTMLButtonElement).disabled = true;

            let successCount = 0;
            let failCount = 0;

            if (!auth.currentUser) {
                // Guest: save all selected sheets to IndexedDB
                for (const key of keysToSave) {
                    const data = this.sessionSheets.get(key);
                    if (!data) continue;
                    try {
                        await GuestStorageManager.saveSheet({
                            key,
                            base64: data.base64,
                            originalBase64: data.base64,
                            width: data.width,
                            height: data.height,
                            frames: data.frames,
                            orientation: data.orientation,
                            animation: data.animation,
                            interval: data.interval,
                            palette: [...(data.palette || [])],
                            savedAt: Date.now()
                        });
                        successCount++;
                        data.originalBase64 = data.base64;
                        data.isDirty = false;
                        if (data.history) {
                            data.baseSnapshot = { 
                                ...data.history[data.historyIndex],
                                animation: data.animation,
                                interval: data.interval,
                                palette: [...(data.palette || [])]
                            };
                        }
                        this.sessionSheets.set(key, data);
                    } catch (e) {
                        failCount++;
                        console.error(`[SpriteLab] IndexedDB save failed for ${key}:`, e);
                    }
                }
            } else {
                // Authenticated: upload to API
                for (const key of keysToSave) {
                    const data = this.sessionSheets.get(key);
                    if (!data) continue;
                    const parts = key.split(':');
                    const metadata = {
                        width: data.width, height: data.height, frames: data.frames,
                        orientation: data.orientation, animation: data.animation, interval: data.interval,
                        palette: [...(data.palette || [])], savedAt: Date.now()
                    };
                    const result = await this.apiManager.uploadSprite(parts[0], parts[1], parts[2], data.base64, metadata);
                    if (result.success) {
                        successCount++;
                        data.originalBase64 = data.base64;
                        data.isDirty = false;
                        if (data.history) {
                            data.baseSnapshot = { 
                                ...data.history[data.historyIndex],
                                animation: data.animation,
                                interval: data.interval,
                                palette: [...(data.palette || [])]
                            };
                        }
                        this.sessionSheets.set(key, data);
                    } else {
                        failCount++;
                        console.error(`Failed to save ${key}: ${result.error}`);
                    }
                }
            }

            (newConfirm as HTMLButtonElement).innerText = this.t('modal_save_selected');
            (newConfirm as HTMLButtonElement).disabled = false;
            modal.style.display = 'none';
            this.updateButtonStates();
            alert(this.t('msg_multi_save_result').replace('{0}', successCount.toString()).replace('{1}', failCount.toString()));
            if (this.currentBase64) {
                const currentKey = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
                if (keysToSave.includes(currentKey)) {
                    this.originalBase64 = this.currentBase64;
                    this.checkDirty();
                }
            }
        });
    }
    handleDownload() { console.log('handleDownload called');
        if (!this.currentBase64) return alert(this.t('err_no_content'));
        const defaultFilename = `${this.itemId}_${this.currentSheet}`;
        const modal = new ExportModal({
            defaultFilename,
            t: this.t.bind(this),
            onExport: (filename, format) => {
                this.executeExport(filename, format);
            }
        });
        modal.show();
    }

    private async executeExport(filename: string, format: 'png' | 'gif') {
        let blobUrl: string = await this.generateExportFile(format);

        const ext = format === 'png' ? '.png' : '.gif';
        const finalName = filename.endsWith(ext) ? filename : filename + ext;

        try {
            if ('showSaveFilePicker' in window) {
                const fileHandle = await (window as any).showSaveFilePicker({
                    suggestedName: finalName,
                    types: [{
                        description: format === 'png' ? 'PNG Image' : 'GIF Image',
                        accept: { [format === 'png' ? 'image/png' : 'image/gif']: [ext] },
                    }],
                });
                const writable = await fileHandle.createWritable();
                const response = await fetch(blobUrl);
                await response.body.pipeTo(writable);
            } else {
                // Fallback for Firefox and others
                throw new Error("showSaveFilePicker not available");
            }
        } catch (err: any) {
            // If the user cancelled the picker, abort silently.
            if (err.name !== 'AbortError') {
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = finalName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        }
    }

    private generateExportFile(format: 'png' | 'gif'): Promise<string> {
        return new Promise((resolve) => {
            const wInput = this.container.querySelector('#sl-meta-width') as HTMLInputElement;
            const hInput = this.container.querySelector('#sl-meta-height') as HTMLInputElement;
            const fInput = this.container.querySelector('#sl-meta-frames') as HTMLInputElement;
            const frameW = parseInt(wInput.value) || 32;
            const frameH = parseInt(hInput.value) || 32;
            const frameCount = parseInt(fInput.value) || 3;
            
            const key = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
            const sheetData = this.sessionSheets.get(key);
            const animType = sheetData?.animation || 'sequence';
            const interval = sheetData?.interval || 1.0;
            // PIXI animation speed logic -> interval in ms
            const delayMs = interval * 1000; 

            const img = new Image();
            img.onload = () => {
                const isHorz = img.width >= img.height;
                const baseFrames: HTMLCanvasElement[] = [];
                
                // Extract frames into separate canvases
                for (let i = 0; i < frameCount; i++) {
                    const canvas = document.createElement('canvas');
                    canvas.width = frameW;
                    canvas.height = frameH;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) continue;
                    
                    if (isHorz) {
                        ctx.drawImage(img, i * frameW, 0, frameW, frameH, 0, 0, frameW, frameH);
                    } else {
                        ctx.drawImage(img, 0, i * frameH, frameW, frameH, 0, 0, frameW, frameH);
                    }
                    baseFrames.push(canvas);
                }

                let sequence: HTMLCanvasElement[] = [];
                if (animType === 'bounce') {
                    sequence = [...baseFrames];
                    for (let i = baseFrames.length - 2; i > 0; i--) {
                        sequence.push(baseFrames[i]);
                    }
                } else if (animType === 'random') {
                    const targetFrames = baseFrames.length * 10;
                    for (let i = 0; i < targetFrames; i++) {
                        const rndIndex = Math.floor(Math.random() * baseFrames.length);
                        sequence.push(baseFrames[rndIndex]);
                    }
                } else {
                    sequence = [...baseFrames];
                }                // Wait a tick before creating Export in case UI blocked
                setTimeout(() => {
                    if (format === 'png') {
                        const outCanvas = document.createElement('canvas');
                        outCanvas.width = frameW * sequence.length;
                        outCanvas.height = frameH;
                        const outCtx = outCanvas.getContext('2d');
                        if (outCtx) {
                            sequence.forEach((c, idx) => {
                                outCtx.drawImage(c, idx * frameW, 0);
                            });
                        }
                        outCanvas.toBlob((blob) => {
                            if (blob) {
                                resolve(URL.createObjectURL(blob));
                            } else {
                                resolve(this.currentBase64 || '');
                            }
                        }, 'image/png');
                    } else {
                        const gif = new GIF({
                            workers: 2,
                            quality: 10,
                            repeat: 0,
                            background: '#FF00FF',
                            transparent: 0xFF00FF,
                            workerScript: '/gif.worker.js',
                            width: frameW,
                            height: frameH
                        });

                        sequence.forEach(frameCanvas => {
                            const ctx = frameCanvas.getContext('2d');
                            if (ctx) {
                                const imgData = ctx.getImageData(0, 0, frameW, frameH);
                                const data = imgData.data;
                                for (let i = 0; i < data.length; i += 4) {
                                    // Only replace fully transparent pixels with magenta
                                    if (data[i + 3] === 0) {
                                        data[i] = 255;
                                        data[i + 1] = 0;
                                        data[i + 2] = 255;
                                        data[i + 3] = 255;
                                    }
                                }
                                ctx.putImageData(imgData, 0, 0);
                            }
                            gif.addFrame(frameCanvas, { delay: delayMs });
                        });

                        gif.on('finished', (blob: Blob) => {
                            resolve(URL.createObjectURL(blob));
                        });

                        gif.render();
                    }
                }, 10);
            };
            img.src = this.currentBase64 || '';
        });
    }   
    public async loadSpriteSheets(spriteName: string, force = false) {
        if (!force && this.loadingSheetsPromise.has(spriteName)) {
            logDev(`[SpriteLab] loadSpriteSheets: dedup hit for ${spriteName}`);
            return this.loadingSheetsPromise.get(spriteName);
        }
        if (force) this.loadingSheetsPromise.delete(spriteName);
        const promise = this.loadSpriteSheetsInternal(spriteName);
        this.loadingSheetsPromise.set(spriteName, promise);
        return promise;
    }

    private async loadSpriteSheetsInternal(spriteName: string) {
        logDev(`[SpriteLab] loadSpriteSheetsInternal: ${spriteName}`);
        try {
            const user = auth.currentUser;
            if (!user) {
                // Guest: derive sheet list from IndexedDB
                logDev(`[SpriteLab] loadSpriteSheetsInternal: guest — loading sheets from IDB for ${this.projectId}:${spriteName}`);
                try {
                    const sheets = await GuestStorageManager.getSheetsForSprite(this.projectId, spriteName);
                    this.parsedSheets = sheets.length > 0 ? sheets : ['main'];
                } catch {
                    this.parsedSheets = ['main'];
                }
                if (!this.currentSheet || !this.parsedSheets.includes(this.currentSheet)) {
                    this.currentSheet = this.parsedSheets[0] || 'main';
                }
                this.renderTabs(this.parsedSheets);
                return;
            }
            const idToken = await user.getIdToken();
            const headers: HeadersInit = {};
            if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
            const apiUrl = (import.meta as any).env.VITE_API_URL ? (import.meta as any).env.VITE_API_URL : '';
            const url = `${apiUrl}/api/sprite-lab/projects/${this.projectId}/sprites/${spriteName}/sheets`;
            logDev(`[SpriteLab] loadSpriteSheetsInternal fetching: ${url}`);
            const res = await fetch(url, { headers });
            logDev(`[SpriteLab] loadSpriteSheetsInternal response: ${res.status}`);
            if (!res.ok) throw new Error(`Failed to load sheets: ${res.statusText}`);
            const sheets = await res.json();
            // Server returns string[] (doc IDs), not {name}[] — handle both defensively
            this.parsedSheets = sheets.length > 0
                ? sheets.map((s: any) => (typeof s === 'string' ? s : s.name)).filter(Boolean)
                : ['main'];
            logDev(`[SpriteLab] loadSpriteSheetsInternal parsed: [${this.parsedSheets.join(', ')}], currentSheet: ${this.currentSheet}`);
            if (!this.currentSheet || !this.parsedSheets.includes(this.currentSheet)) {
                this.currentSheet = this.parsedSheets[0] || 'main';
                logDev(`[SpriteLab] loadSpriteSheetsInternal: reset currentSheet to '${this.currentSheet}'`);
            }
            this.renderTabs(this.parsedSheets);
        } catch (e) {
            logDev(`[SpriteLab] loadSpriteSheetsInternal error: ${e}`);
            console.error('Failed to load sheets', e);
            this.parsedSheets = ['main'];
            if (!this.currentSheet) this.currentSheet = 'main';
            this.renderTabs(this.parsedSheets);
        } finally {
            this.loadingSheetsPromise.delete(spriteName);
        }
    }
    private updateManagePanelUI() {
        const hasSprite = !!this.itemId;
        const deleteSpriteAction = this.container.querySelector('#sl-delete-sprite-action') as HTMLElement;
        if (deleteSpriteAction) {
            deleteSpriteAction.style.display = hasSprite ? 'flex' : 'none';
        }
        const mgmtPanel = this.container.querySelector('#sl-management-panel') as HTMLElement;
        if (mgmtPanel) mgmtPanel.style.display = 'none';
    }
    private updateSelectionHighlight(fw: number, fh: number) {
        const svg = this.container.querySelector('#sl-sheet-grid');
        if (!svg) return;
        let outer = document.getElementById('sl-selection-highlight') as SVGRectElement | null;
        let inner = document.getElementById('sl-selection-highlight-inner') as SVGRectElement | null;
        if (!this.selectedFrame || fw === 0 || fh === 0) {
            if (outer) outer.style.display = 'none';
            if (inner) inner.style.display = 'none';
            return;
        }
        if (!outer) {
            outer = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            outer.id = 'sl-selection-highlight';
            outer.setAttribute('fill', 'rgba(99, 102, 241, 0.1)'); // Subtle blue tint
            outer.setAttribute('stroke', 'transparent');
            outer.setAttribute('stroke-width', '8');
            outer.setAttribute('vector-effect', 'non-scaling-stroke');
            outer.style.pointerEvents = 'none';
            svg.appendChild(outer);
        }
        if (!inner) {
            inner = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            inner.id = 'sl-selection-highlight-inner';
            inner.setAttribute('fill', 'none');
            inner.setAttribute('stroke', '#0078d4'); // Primary Blue
            inner.setAttribute('stroke-width', '6');
            inner.setAttribute('stroke-dasharray', '16, 16');
            inner.setAttribute('vector-effect', 'non-scaling-stroke');
            inner.style.pointerEvents = 'none';
            if (!document.getElementById('sl-svg-selection-anim')) {
                const s = document.createElement('style');
                s.id = 'sl-svg-selection-anim';
                s.textContent = `
                    #sl-selection-highlight-inner {
                        animation: sl-svg-march 0.5s linear infinite;
                    }
                    @keyframes sl-svg-march {
                        to { stroke-dashoffset: 32; }
                    }
                `;
                document.head.appendChild(s);
            }
            svg.appendChild(inner);
        }

        let x = 0;
        let y = 0;
        let pWidth = 0;
        let pHeight = 0;

        if (this.selectedFrame.free && this.selectedFrame.px !== undefined) {
            x = this.selectedFrame.px;
            y = this.selectedFrame.py!;
            pWidth = this.selectedFrame.pw!;
            pHeight = this.selectedFrame.ph!;
        } else {
            x = this.selectedFrame.col * fw;
            y = this.selectedFrame.row * fh;
            pWidth = fw;
            pHeight = fh;
        }

        const scale = this.transformState?.scale || 1;
        const inset = 3.5 / scale;
        outer.setAttribute('x', `${x + inset}`);
        outer.setAttribute('y', `${y + inset}`);
        outer.setAttribute('width', `${pWidth - inset * 2}`);
        outer.setAttribute('height', `${pHeight - inset * 2}`);
        outer.style.display = 'block';

        inner.setAttribute('x', `${x + inset}`);
        inner.setAttribute('y', `${y + inset}`);
        inner.setAttribute('width', `${pWidth - inset * 2}`);
        inner.setAttribute('height', `${pHeight - inset * 2}`);
        inner.style.display = 'block';
    }
    public async loadProjectList(force = false) {
        logDev(`[SpriteLab] loadProjectList() called. Promise status: ${!!this.loadingProjectPromise}, force: ${force}`);
        if (this.loadingProjectPromise) {
            if (force) {
                logDev('[SpriteLab] loadProjectList() - Force-resetting existing promise');
                this.loadingProjectPromise = null;
            } else {
                logDev('[SpriteLab] loadProjectList() - Returning existing promise');
                return this.loadingProjectPromise;
            }
        }
        this.loadingProjectPromise = this.loadProjectListInternal();
        return this.loadingProjectPromise;
    }

    private async loadProjectListInternal() {
        const t0 = performance.now();
        const T = () => `+${(performance.now() - t0).toFixed(0)}ms`;
        console.log(`[SpriteLab] [${T()}] loadProjectListInternal start`);
        this.isLoadingProjects = true;
        try {
            const user = auth.currentUser;
            const headers: HeadersInit = {};
            if (user) {
                logDev('[SpriteLab] loadProjectListInternal: Getting ID token...');
                const idToken = await user.getIdToken();
                logDev('[SpriteLab] loadProjectListInternal: Got ID token');
                headers['Authorization'] = `Bearer ${idToken}`;
            } else if (hasSession()) {
                // User has a prior session but Firebase hasn't resolved yet.
                // onAuthStateChanged will fire shortly and call loadProjectList(true).
                // Skip the 448ms IDB open — just render a loading state and bail.
                console.log(`[SpriteLab] [${T()}] hasSession=true but no currentUser yet — waiting for Firebase`);
                this.isLoadingProjects = false;
                return;
            } else {
                // True guest: kick off IDB warmup in background for subsequent calls,
                // then do the real guest tree load.
                GuestStorageManager.warmup();
                console.log(`[SpriteLab] [${T()}] guest — calling getProjectTree()`);
                try {
                    const guestTree = await GuestStorageManager.getProjectTree();
                    console.log(`[SpriteLab] [${T()}] getProjectTree() done, ${guestTree.length} projects`);
                    if (guestTree.length === 0) {
                        // First-time guest: seed a default local project
                        const now = new Date().toISOString();
                        guestTree.push({ name: 'project', sprites: ['sprite'], createdAt: now, updatedAt: now });

                        // Save a blank IDB record so the project persists in getProjectTree()
                        const idbKey = `project:sprite:main`;
                        const gw = 32, gh = 32, gf = 1, go = 'horizontal';
                        const tmpCanvas = document.createElement('canvas');
                        tmpCanvas.width = gw; tmpCanvas.height = gh;
                        const blankB64 = tmpCanvas.toDataURL();
                        GuestStorageManager.saveSheet({
                            key: idbKey, base64: blankB64, originalBase64: blankB64,
                            width: gw, height: gh, frames: gf, orientation: go,
                            animation: 'sequence', interval: 0.2,
                            palette: [...(this.colorPicker?.getPalette() || [])],
                            savedAt: Date.now()
                        }).catch(e => console.error('[SpriteLab] Failed to initialize project in IDB', e));
                    }
                    this.projectTree = guestTree;
                    this.projects = guestTree.map(p => p.name);
                    if (!this.initializedExpansion) {
                        this.projectTree.forEach(p => this.expandedProjects.add(p.name));
                        this.initializedExpansion = true;
                    }
                    if (!this.projectTree.find(p => p.name === this.projectId)) {
                        this.projectId = this.projectTree[0]?.name || 'project';
                    }
                    const curProj = this.projectTree.find(p => p.name === this.projectId);
                    if (curProj && !curProj.sprites.includes(this.itemId)) {
                        this.itemId = curProj.sprites[0] || '';
                    }
                    if (this.projectId && !this.expandedProjects.has(this.projectId)) {
                        this.expandedProjects.add(this.projectId);
                    }
                } catch (idbErr) {
                    logDev('[SpriteLab] loadProjectListInternal: IndexedDB error, using default', idbErr);
                    this.projectTree = [{ name: 'project', sprites: ['sprite'] }];
                    this.projects = ['project'];
                    this.projectId = 'project';
                }
                // NOTE: Do NOT call renderExplorer() here.
                // The finally block always runs (even after return) and will call renderExplorer()
                // once. A second call here would rebuild the DOM and destroy any queued feedback animations.
                if (this.itemId) {
                    this.lastItemId = this.itemId;
                    this.lastProjectId = this.projectId;
                    if (!this.currentSheet) this.currentSheet = 'main';
                    const sessionKey = `${this.projectId}:${this.itemId}:${this.currentSheet}`;
                    if (!this.sessionSheets.has(sessionKey)) {
                        this.checkSpriteAvailability();
                    } else {
                        // Sheet already loaded — just refresh the UI without touching the canvas
                        this.renderTabs(this.parsedSheets);
                        this.renderHistoryList();
                        this.updateButtonStates();
                    }
                }
                return;
            }
            const apiUrl = (import.meta as any).env.VITE_API_URL ? (import.meta as any).env.VITE_API_URL : '';
            logDev(`[SpriteLab] loadProjectListInternal: Fetching from ${apiUrl}/api/sprite-lab/projects?tree=true`);
            const res = await fetch(`${apiUrl}/api/sprite-lab/projects?tree=true`, { headers });
            logDev(`[SpriteLab] loadProjectListInternal: Fetch result status: ${res.status}`);
            if (!res.ok) throw new Error(`Failed to load projects: ${res.statusText}`);
            const data = await res.json();
            logDev(`[SpriteLab] loadProjectListInternal: Parse JSON success`, data);
            logDev('[SpriteLab] Received Project Tree:', data);
            this.projectTree = data.map((p: any) => ({
                name: p.name,
                createdAt: p.createdAt,
                updatedAt: p.updatedAt,
                sprites: p.sprites || []
            }));
            this.projects = this.projectTree.map(p => p.name);
            if (!this.initializedExpansion && this.projectTree.length > 0) {
                this.projectTree.forEach(p => this.expandedProjects.add(p.name));
                this.initializedExpansion = true;
            }
            let currentProj = this.projectTree.find(p => p.name === this.projectId);
            if (!currentProj && this.projectTree.length > 0) {
                this.projectId = this.projectTree[0].name;
                currentProj = this.projectTree[0];
            }
            if (currentProj) {
                if (!currentProj.sprites.includes(this.itemId)) {
                    if (currentProj.sprites.length > 0) {
                        logDev(`[SpriteLab] Current sprite '${this.itemId}' not found in '${this.projectId}'. Switching to '${currentProj.sprites[0]}'`);
                        this.itemId = currentProj.sprites[0];
                    } else {
                        this.itemId = '';
                    }
                }
            } else {
                this.projectId = '';
                this.itemId = '';
            }
            if (this.projectId && !this.expandedProjects.has(this.projectId)) {
                this.expandedProjects.add(this.projectId);
            }
            if (this.itemId) {
                this.lastItemId = this.itemId;
                this.lastProjectId = this.projectId;
                if (!this.currentSheet) this.currentSheet = 'main';
                logDev(`[SpriteLab] loadProjectListInternal: checking sprite availability for ${this.projectId}:${this.itemId}:${this.currentSheet}`);
                this.checkSpriteAvailability();
            }
        } catch (e) {
            console.error("Failed to load project list", e);
            this.projects = ['main'];
        } finally {
            this.isLoadingProjects = false;
            this.renderExplorer();
            this.loadingProjectPromise = null;
        }
    }
    async handleCreateProject(nameParam?: string) {
        try {
            let name = nameParam;
            if (!name) {
                const input = this.container.querySelector('#sl-new-project-name') as HTMLInputElement;
                name = input.value.trim();
            }
            if (!name) return alert(this.t('err_name_empty'));
            if (this.projectTree.find(p => p.name === name)) return alert(this.t('err_project_exists'));

            if (!auth.currentUser) {
                // Guest: create project locally (no API call)
                const now = new Date().toISOString();
                this.projectTree.push({ name, sprites: ['sprite'], createdAt: now, updatedAt: now });
                this.projects.push(name);
                this.expandedProjects.add(name);
                this.projectId = name;
                this.itemId = 'sprite';
                this.currentSheet = 'main';

                // Save a blank IDB record so the project persists in getProjectTree()
                const idbKey = `${name}:sprite:main`;
                const gw = 32, gh = 32, gf = 1, go = 'horizontal';
                const tmpCanvas = document.createElement('canvas');
                tmpCanvas.width = gw; tmpCanvas.height = gh;
                const blankB64 = tmpCanvas.toDataURL();
                const snap = {
                    base64: blankB64, originalBase64: blankB64,
                    width: gw, height: gh, frames: gf, orientation: go,
                    animation: 'sequence', interval: 0.2, isDirty: false,
                    history: [{ base64: blankB64, width: gw, height: gh, frames: gf, orientation: go, animation: 'sequence', interval: 0.2, palette: [...(this.colorPicker?.getPalette() || [])] }],
                    historyIndex: 0,
                    palette: [...(this.colorPicker?.getPalette() || [])],
                    baseSnapshot: { base64: blankB64, width: gw, height: gh, frames: gf, orientation: go, animation: 'sequence', interval: 0.2, palette: [...(this.colorPicker?.getPalette() || [])] }
                };
                this.sessionSheets.set(idbKey, snap);
                GuestStorageManager.saveSheet({
                    key: idbKey,
                    base64: blankB64,
                    originalBase64: blankB64,
                    width: gw, height: gh, frames: gf, orientation: go,
                    animation: 'sequence', interval: 0.2,
                    palette: [...(this.colorPicker?.getPalette() || [])],
                    savedAt: Date.now()
                }).catch(e => console.error('[SpriteLab] Failed to initialize project in IDB', e));

                (this.container.querySelector('#sl-modal-create-project') as HTMLElement).style.display = 'none';
                const input = this.container.querySelector('#sl-new-project-name') as HTMLInputElement;
                if (input) input.value = '';

                this.renderExplorer();
                this.checkSpriteAvailability(); // Load the blank canvas into the editor
                return;
            }

            const user = auth.currentUser;
            const idToken = await user.getIdToken();
            const apiUrl = (import.meta as any).env.VITE_API_URL ? (import.meta as any).env.VITE_API_URL : '';
            const res = await fetch(`${apiUrl}/api/sprite-lab/projects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken, name })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to create project');
            }
            const data = await res.json();
            this.projectId = data.name;
            this.itemId = 'sprite';
            (this.container.querySelector('#sl-modal-create-project') as HTMLElement).style.display = 'none';
            await this.loadProjectList();
            this.checkSpriteAvailability();
        } catch (e: any) {
            alert(e.message);
        }
    }
    async handleDeleteProject(name: string) {
        try {
            if (!auth.currentUser) {
                // Guest: delete project locally (no API call)
                try {
                    await GuestStorageManager.deleteProjectKeys(name);
                } catch (e) {
                    console.error('[SpriteLab] IDB deleteProject failed:', e);
                }
                this.projectTree = this.projectTree.filter(p => p.name !== name);
                this.projects = this.projects.filter(p => p !== name);
                this.expandedProjects.delete(name);

                // Clear session map of anything related to this project
                const prefix = `${name}:`;
                for (const key of this.sessionSheets.keys()) {
                    if (key.startsWith(prefix)) this.sessionSheets.delete(key);
                }

                this.projectId = 'main';
                this.itemId = 'sprite';
                this.currentSheet = 'main';

                if (this.projectTree.length === 0) {
                    // Re-seed default generic project so UI isn't fully empty
                    const now = new Date().toISOString();
                    this.projectTree.push({ name: 'project', sprites: ['sprite'], createdAt: now, updatedAt: now });
                    this.projects.push('project');
                    this.projectId = 'project';
                    this.expandedProjects.add('project');

                    // Save blank snap to IDB just like in handleCreateProject
                    const idbKey = `project:sprite:main`;
                    const gw = 32, gh = 32, gf = 1, go = 'horizontal';
                    const tmpCanvas = document.createElement('canvas');
                    tmpCanvas.width = gw; tmpCanvas.height = gh;
                    const blankB64 = tmpCanvas.toDataURL();
                    const snap = {
                        base64: blankB64, originalBase64: blankB64,
                        width: gw, height: gh, frames: gf, orientation: go,
                        animation: 'sequence', interval: 0.2, isDirty: false,
                        history: [{ base64: blankB64, width: gw, height: gh, frames: gf, orientation: go, animation: 'sequence', interval: 0.2, palette: [...(this.colorPicker?.getPalette() || [])] }],
                        historyIndex: 0,
                        palette: [...(this.colorPicker?.getPalette() || [])],
                        baseSnapshot: { base64: blankB64, width: gw, height: gh, frames: gf, orientation: go, animation: 'sequence', interval: 0.2, palette: [...(this.colorPicker?.getPalette() || [])] }
                    };
                    this.sessionSheets.set(idbKey, snap);
                    GuestStorageManager.saveSheet({
                        key: idbKey, base64: blankB64, originalBase64: blankB64,
                        width: gw, height: gh, frames: gf, orientation: go,
                        animation: 'sequence', interval: 0.2,
                        palette: [...(this.colorPicker?.getPalette() || [])],
                        savedAt: Date.now()
                    });
                } else if (!this.projectTree.find(p => p.name === this.projectId)) {
                    this.projectId = this.projectTree[0].name;
                    this.itemId = this.projectTree[0].sprites[0] || '';
                }

                this.renderExplorer();
                this.checkSpriteAvailability(); // Load whatever the new target is (the re-seeded local or the first available)
                return;
            }

            const user = auth.currentUser;
            const idToken = await user.getIdToken();
            const apiUrl = (import.meta as any).env.VITE_API_URL ? (import.meta as any).env.VITE_API_URL : '';
            const res = await fetch(`${apiUrl}/api/sprite-lab/projects/${name}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to delete project');
            }
            this.projectId = 'main';
            this.itemId = 'sprite';
            await this.loadProjectList();
            this.checkSpriteAvailability(); // Ensure editor refreshes back to main
        } catch (e: any) {
            alert(e.message);
        }
    }
}





